/**
 * Histórico de inspeções da ficha (#363): do dia mais recente ao mais antigo, com "Corrigir" e
 * "Apagar" por linha. As duas ações pedem `fire_equipment.update` (D10) e ficam desabilitadas,
 * com o motivo escrito na tela, quando o equipamento está fora de uso: o histórico dele fica
 * congelado até reativar.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

import { DataTable, type DataTableColumn } from '../../../components';
import { LockableActionButton } from './LockableActionButton';
import { ValidityChip } from '../../../components/common/ValidityChip';
import type { FireEquipmentLabels, FireInspection } from '../../../types/fireEquipment';
import { formatIsoDate } from '../fireDates';

const TOUCH_TARGET = 44;

export const DECOMMISSIONED_REASON =
  'Equipamento fora de uso: reative-o para registrar, corrigir ou apagar inspeções.';

export interface InspectionHistoryProps {
  inspections: FireInspection[];
  labels: Pick<FireEquipmentLabels, 'result'>;
  /** Equipamento fora de uso: as ações de linha ficam desabilitadas. */
  locked: boolean;
  onCorrect: (inspection: FireInspection) => void;
  onDelete: (inspection: FireInspection) => void;
}

/** Mais recente primeiro; no mesmo dia, a de maior `id`. */
function newestFirst(a: FireInspection, b: FireInspection): number {
  if (a.inspectionDate !== b.inspectionDate) return a.inspectionDate < b.inspectionDate ? 1 : -1;
  return b.id - a.id;
}

export const InspectionHistory: React.FC<InspectionHistoryProps> = ({
  inspections,
  labels,
  locked,
  onCorrect,
  onDelete,
}) => {
  const columns: DataTableColumn<FireInspection>[] = [
    {
      id: 'date',
      label: 'Data',
      primary: true,
      render: (inspection) => formatIsoDate(inspection.inspectionDate),
    },
    {
      id: 'result',
      label: 'Resultado',
      render: (inspection) => (
        <ValidityChip
          situation={inspection.result === 'CONFORME' ? 'EM_DIA' : 'COM_PENDENCIA'}
          label={labels.result[inspection.result]}
        />
      ),
    },
    { id: 'performedBy', label: 'Quem inspecionou', render: (inspection) => inspection.performedBy },
    {
      id: 'observations',
      label: 'Observação',
      width: 280,
      truncate: true,
      render: (inspection) =>
        inspection.observations ? (
          <Typography variant="body1" noWrap title={inspection.observations}>
            {inspection.observations}
          </Typography>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      label: 'Ações',
      render: (inspection) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <LockableActionButton
            acao="fire_equipment.update"
            rotulo="Corrigir"
            lockedReason={locked ? DECOMMISSIONED_REASON : undefined}
            sx={{ minHeight: TOUCH_TARGET }}
            onClick={() => onCorrect(inspection)}
          >
            Corrigir
          </LockableActionButton>
          <LockableActionButton
            acao="fire_equipment.update"
            rotulo="Apagar"
            color="error"
            lockedReason={locked ? DECOMMISSIONED_REASON : undefined}
            sx={{ minHeight: TOUCH_TARGET }}
            onClick={() => onDelete(inspection)}
          >
            Apagar
          </LockableActionButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      {locked && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          {DECOMMISSIONED_REASON}
        </Typography>
      )}
      <DataTable
        embedded
        columns={columns}
        rows={[...inspections].sort(newestFirst)}
        getRowId={(inspection) => inspection.id}
        emptyState={{
          title: 'Nenhuma inspeção registrada',
          description:
            'Registre a primeira inspeção: a próxima passa a ser calculada a partir dela, e o histórico fica guardado para a fiscalização.',
        }}
      />
    </>
  );
};

export default InspectionHistory;
